import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Save, User, Briefcase, Clock, Calendar, Award, MessageCircle } from 'lucide-react';

const ReviewerRegistration: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  const [formData, setFormData] = useState({
    // Personal Information
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    linkedinUrl: '',
    portfolioUrl: '',
    
    // Professional Background
    company: '',
    position: '',
    yearsOfExperience: '',
    currentEmploymentStatus: '',
    industry: '',
    
    // Expertise & Skills
    primaryExpertise: [] as string[],
    secondarySkills: [] as string[],
    industriesWorked: [] as string[],
    toolsAndSoftware: [] as string[],
    
    // Review Experience
    previousReviewExperience: '',
    mentoringExperience: '',
    reviewingMotivation: '',
    
    // Availability & Preferences
    timezone: '',
    maxReviewsPerDay: '',
    preferredReviewDuration: '',
    availableDays: [] as string[],
    availableTimeSlots: [] as string[],
    
    // Review Preferences
    preferredReviewTypes: [] as string[],
    experienceLevels: [] as string[],
    reviewFormat: '',
    
    // Bio & Additional Info
    professionalBio: '',
    linkedinProfile: '',
    additionalComments: '',
    
    // Agreements
    agreeToTerms: false,
    agreeToCommitment: false,
    agreeToGuidelines: false,
    agreeToRecording: false,
  });

  const expertiseOptions = [
    'UI/UX Design', 'Graphic Design', 'Web Development', 'Mobile Development',
    'Product Management', 'Data Science', 'Marketing', 'Content Strategy',
    'Branding', 'Illustration', 'Photography', 'Video Production',
    'Frontend Development', 'Backend Development', 'Full-Stack Development',
    'DevOps', 'Game Design', 'Motion Graphics', 'User Research'
  ];

  const industryOptions = [
    'Technology', 'Healthcare', 'Finance', 'Education', 'E-commerce',
    'Media & Entertainment', 'Non-profit', 'Government', 'Consulting',
    'Automotive', 'Real Estate', 'Travel & Hospitality', 'Fashion',
    'Food & Beverage', 'Sports & Recreation', 'Other'
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const toggleArrayItem = (field: keyof typeof formData, value: string) => {
    const currentArray = formData[field] as string[];
    const newArray = currentArray.includes(value)
      ? currentArray.filter(item => item !== value)
      : [...currentArray, value];
    
    setFormData(prev => ({ ...prev, [field]: newArray }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const registrationData = {
        eventId,
        type: 'reviewer',
        formData,
        submittedAt: new Date(),
      };

      console.log('Submitting reviewer registration:', registrationData);
      
      // Navigate to success page
      navigate(`/register/success?type=reviewer&event=${eventId}`);
    } catch (error) {
      console.error('Error submitting registration:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Mock event data - in real app, fetch from API
  const event = {
    title: 'Fall 2025 Portfolio Review Session',
    description: 'Comprehensive portfolio review sessions for design students with industry professionals',
    startDate: new Date('2025-09-15'),
    endDate: new Date('2025-09-17'),
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 mb-8">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Volunteer as a Reviewer</h1>
            <h2 className="text-xl font-semibold text-primary-600 mb-4">{event.title}</h2>
            <p className="text-lg text-gray-600 mb-4">{event.description}</p>
            <div className="flex justify-center items-center space-x-4 text-sm text-gray-500">
              <div className="flex items-center">
                <Calendar className="h-4 w-4 mr-1" />
                {event.startDate.toLocaleDateString()} - {event.endDate.toLocaleDateString()}
              </div>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Personal Information */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
              <User className="h-5 w-5 mr-2" />
              Personal Information
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  First Name *
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  required
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Last Name *
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  required
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  LinkedIn Profile *
                </label>
                <input
                  type="url"
                  name="linkedinUrl"
                  value={formData.linkedinUrl}
                  onChange={handleInputChange}
                  required
                  placeholder="https://linkedin.com/in/yourprofile"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Portfolio/Website URL
                </label>
                <input
                  type="url"
                  name="portfolioUrl"
                  value={formData.portfolioUrl}
                  onChange={handleInputChange}
                  placeholder="https://your-portfolio.com"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>
          </div>

          {/* Professional Background */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
              <Briefcase className="h-5 w-5 mr-2" />
              Professional Background
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Current Company *
                </label>
                <input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  required
                  placeholder="e.g., Google, Apple, Freelance"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Current Position *
                </label>
                <input
                  type="text"
                  name="position"
                  value={formData.position}
                  onChange={handleInputChange}
                  required
                  placeholder="e.g., Senior UX Designer, Product Manager"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Years of Professional Experience *
                </label>
                <select
                  name="yearsOfExperience"
                  value={formData.yearsOfExperience}
                  onChange={handleInputChange}
                  required
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Select experience level</option>
                  <option value="1-2">1-2 years</option>
                  <option value="3-5">3-5 years</option>
                  <option value="5-10">5-10 years</option>
                  <option value="10+">10+ years</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Current Employment Status
                </label>
                <select
                  name="currentEmploymentStatus"
                  value={formData.currentEmploymentStatus}
                  onChange={handleInputChange}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Select status</option>
                  <option value="full-time">Full-time Employee</option>
                  <option value="part-time">Part-time Employee</option>
                  <option value="contractor">Contractor</option>
                  <option value="freelancer">Freelancer</option>
                  <option value="consultant">Consultant</option>
                  <option value="entrepreneur">Entrepreneur</option>
                </select>
              </div>
            </div>
          </div>

          {/* Expertise & Skills */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
              <Award className="h-5 w-5 mr-2" />
              Expertise & Skills
            </h2>
            
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Primary Areas of Expertise * (Select all that apply)
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {expertiseOptions.map((skill) => (
                    <label key={skill} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.primaryExpertise.includes(skill)}
                        onChange={() => toggleArrayItem('primaryExpertise', skill)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-900">{skill}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Industries You've Worked In (Select all that apply)
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {industryOptions.map((industry) => (
                    <label key={industry} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.industriesWorked.includes(industry)}
                        onChange={() => toggleArrayItem('industriesWorked', industry)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-900">{industry}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Review Experience & Motivation */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
              <MessageCircle className="h-5 w-5 mr-2" />
              Review Experience & Motivation
            </h2>
            
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Previous Review/Mentoring Experience
                </label>
                <textarea
                  name="previousReviewExperience"
                  value={formData.previousReviewExperience}
                  onChange={handleInputChange}
                  rows={3}
                  placeholder="Describe any previous experience reviewing portfolios, mentoring, or providing feedback..."
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Why do you want to volunteer as a reviewer? *
                </label>
                <textarea
                  name="reviewingMotivation"
                  value={formData.reviewingMotivation}
                  onChange={handleInputChange}
                  required
                  rows={4}
                  placeholder="What motivates you to give back to the design/development community? What do you hope to achieve through this volunteer experience?"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Professional Bio *
                </label>
                <textarea
                  name="professionalBio"
                  value={formData.professionalBio}
                  onChange={handleInputChange}
                  required
                  rows={4}
                  placeholder="Write a brief professional bio that will be shared with reviewees (2-3 sentences about your background and expertise)..."
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>
          </div>

          {/* Availability & Preferences */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
              <Clock className="h-5 w-5 mr-2" />
              Availability & Preferences
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Timezone *
                </label>
                <select
                  name="timezone"
                  value={formData.timezone}
                  onChange={handleInputChange}
                  required
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Select your timezone</option>
                  <option value="PST">Pacific Standard Time (PST)</option>
                  <option value="MST">Mountain Standard Time (MST)</option>
                  <option value="CST">Central Standard Time (CST)</option>
                  <option value="EST">Eastern Standard Time (EST)</option>
                  <option value="GMT">Greenwich Mean Time (GMT)</option>
                  <option value="CET">Central European Time (CET)</option>
                  <option value="JST">Japan Standard Time (JST)</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Maximum Reviews Per Day *
                </label>
                <select
                  name="maxReviewsPerDay"
                  value={formData.maxReviewsPerDay}
                  onChange={handleInputChange}
                  required
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Select maximum</option>
                  <option value="1">1 review per day</option>
                  <option value="2">2 reviews per day</option>
                  <option value="3">3 reviews per day</option>
                  <option value="4">4 reviews per day</option>
                  <option value="5+">5+ reviews per day</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Preferred Review Duration
                </label>
                <select
                  name="preferredReviewDuration"
                  value={formData.preferredReviewDuration}
                  onChange={handleInputChange}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">No preference</option>
                  <option value="30">30 minutes</option>
                  <option value="45">45 minutes</option>
                  <option value="60">60 minutes</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Preferred Review Format
                </label>
                <select
                  name="reviewFormat"
                  value={formData.reviewFormat}
                  onChange={handleInputChange}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">No preference</option>
                  <option value="video-call">Video Call</option>
                  <option value="phone-call">Phone Call</option>
                  <option value="in-person">In-Person (if available)</option>
                </select>
              </div>
            </div>

            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Available Days * (Select all that apply)
              </label>
              <div className="grid grid-cols-3 md:grid-cols-7 gap-3">
                {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day) => (
                  <label key={day} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.availableDays.includes(day)}
                      onChange={() => toggleArrayItem('availableDays', day)}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-900">{day.slice(0, 3)}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Preferred Time Slots * (Select all that apply)
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  'Early Morning (6-9 AM)',
                  'Morning (9 AM-12 PM)',
                  'Afternoon (12-3 PM)',
                  'Late Afternoon (3-6 PM)',
                  'Evening (6-9 PM)',
                  'Night (9 PM-12 AM)'
                ].map((timeSlot) => (
                  <label key={timeSlot} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.availableTimeSlots.includes(timeSlot)}
                      onChange={() => toggleArrayItem('availableTimeSlots', timeSlot)}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-900">{timeSlot}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Terms and Agreements */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Reviewer Commitment & Agreements</h2>
            
            <div className="space-y-4">
              <div className="flex items-start">
                <input
                  type="checkbox"
                  id="agreeToTerms"
                  name="agreeToTerms"
                  checked={formData.agreeToTerms}
                  onChange={handleInputChange}
                  required
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded mt-1"
                />
                <label htmlFor="agreeToTerms" className="ml-3 block text-sm text-gray-900">
                  I agree to the reviewer terms and conditions *
                </label>
              </div>

              <div className="flex items-start">
                <input
                  type="checkbox"
                  id="agreeToCommitment"
                  name="agreeToCommitment"
                  checked={formData.agreeToCommitment}
                  onChange={handleInputChange}
                  required
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded mt-1"
                />
                <label htmlFor="agreeToCommitment" className="ml-3 block text-sm text-gray-900">
                  I commit to attending my scheduled review sessions and providing 24-hour notice if I need to cancel *
                </label>
              </div>

              <div className="flex items-start">
                <input
                  type="checkbox"
                  id="agreeToGuidelines"
                  name="agreeToGuidelines"
                  checked={formData.agreeToGuidelines}
                  onChange={handleInputChange}
                  required
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded mt-1"
                />
                <label htmlFor="agreeToGuidelines" className="ml-3 block text-sm text-gray-900">
                  I agree to follow the reviewer guidelines and provide constructive, professional feedback *
                </label>
              </div>

              <div className="flex items-start">
                <input
                  type="checkbox"
                  id="agreeToRecording"
                  name="agreeToRecording"
                  checked={formData.agreeToRecording}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded mt-1"
                />
                <label htmlFor="agreeToRecording" className="ml-3 block text-sm text-gray-900">
                  I consent to review sessions being recorded for quality assurance purposes
                </label>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="text-center">
            <button
              type="submit"
              disabled={isLoading}
              className="inline-flex items-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Submitting Application...
                </>
              ) : (
                <>
                  <Save className="h-5 w-5 mr-2" />
                  Submit Reviewer Application
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ReviewerRegistration;